/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import type { VacationSchedule } from "@lemmonhq/types";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { VacationDayForm } from "./VacationDayForm";

interface VacationScheduleProps {
  schedule: VacationSchedule;
  onUpdate: (schedule: VacationSchedule) => void;
}

type EditingItem = {
  index: number;
  item: VacationSchedule["items"][0];
} | null;

export const VacationScheduleComponent = defineComponent<VacationScheduleProps>(
  {
    name: "VacationSchedule",
    props: ["schedule", "onUpdate"],
    setup(props) {
      const { t } = useLocalization();
      const editingItem = ref<EditingItem>(null);

      const openNewDay = () => {
        editingItem.value = {
          index: -1,
          item: {
            date: new Date().toISOString().split("T")[0],
            reason: "",
            payload: {
              type: "closed_all_day",
            },
          },
        };
      };

      const openEditDay = (index: number) => {
        editingItem.value = {
          index,
          item: JSON.parse(JSON.stringify(props.schedule.items[index])),
        };
      };

      const closeSheet = () => {
        editingItem.value = null;
      };

      const saveDay = () => {
        if (!editingItem.value) return;

        const items = [...props.schedule.items];
        if (editingItem.value.index === -1) {
          items.push(editingItem.value.item);
        } else {
          items[editingItem.value.index] = editingItem.value.item;
        }
        props.onUpdate({ items });
        closeSheet();
      };

      const removeVacationDay = (index: number) => {
        props.onUpdate({
          items: props.schedule.items.filter((_, i) => i !== index),
        });
      };

      const updateEditingField = (
        field: "date" | "reason" | "guestNote",
        value: string,
      ) => {
        if (!editingItem.value) return;
        editingItem.value.item[field] = value;
      };

      const toggleDayType = () => {
        if (!editingItem.value) return;
        const item = editingItem.value.item;

        if (item.payload.type === "closed_all_day") {
          editingItem.value.item = {
            ...item,
            payload: {
              type: "intervals",
              intervals: [
                {
                  start: "09:00",
                  end: "17:00",
                  services: {
                    "dine-in": true,
                    delivery: true,
                    takeaway: true,
                  },
                },
              ],
            },
          };
        } else {
          editingItem.value.item = {
            ...item,
            payload: {
              type: "closed_all_day",
            },
          };
        }
      };

      const toggleService = (
        service: "dine-in" | "takeaway" | "delivery",
        intervalIndex?: number,
      ) => {
        if (!editingItem.value) return;
        const item = editingItem.value.item;

        if (item.payload.type === "intervals" && intervalIndex !== undefined) {
          const intervals = [...item.payload.intervals];
          const interval = intervals[intervalIndex];
          const services = interval.services || {};
          intervals[intervalIndex] = {
            ...interval,
            services: {
              ...services,
              [service]: !services[service],
            },
          };
          editingItem.value.item = {
            ...item,
            payload: {
              ...item.payload,
              intervals,
            },
          };
        }
      };

      const addInterval = () => {
        if (!editingItem.value) return;
        const item = editingItem.value.item;

        if (item.payload.type === "intervals") {
          editingItem.value.item = {
            ...item,
            payload: {
              ...item.payload,
              intervals: [
                ...item.payload.intervals,
                {
                  start: "09:00",
                  end: "17:00",
                  services: {},
                },
              ],
            },
          };
        }
      };

      const removeInterval = (intervalIndex: number) => {
        if (!editingItem.value) return;
        const item = editingItem.value.item;

        if (item.payload.type === "intervals") {
          editingItem.value.item = {
            ...item,
            payload: {
              ...item.payload,
              intervals: item.payload.intervals.filter(
                (_, i) => i !== intervalIndex,
              ),
            },
          };
        }
      };

      const updateInterval = (
        intervalIndex: number,
        field: "start" | "end",
        value: string,
      ) => {
        if (!editingItem.value) return;
        const item = editingItem.value.item;

        if (item.payload.type === "intervals") {
          const intervals = [...item.payload.intervals];
          intervals[intervalIndex] = {
            ...intervals[intervalIndex],
            [field]: value,
          };
          editingItem.value.item = {
            ...item,
            payload: {
              ...item.payload,
              intervals,
            },
          };
        }
      };

      const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      };

      const ServiceBadge = ({ service }: { service: string }) => (
        <span class="badge badge-sm badge-primary">{service}</span>
      );

      return () => (
        <>
          <div class="l-box-shadow rounded rounded-2xl shadow-xl bg-base-100 border border-base-100">
            <div class="card-body">
              <div class="flex items-center gap-3 mb-6">
                <div class="p-3 bg-warning/10 rounded-xl">
                  <span class="material-symbols-outlined text-warning text-2xl">
                    beach_access
                  </span>
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-semibold text-accent">
                    Vacation Schedule
                  </h3>
                  <p class="text-sm text-base-content">
                    Set special hours or closures for holidays and vacations
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openNewDay}
                  class="btn btn-primary btn-sm gap-2"
                >
                  <span class="material-symbols-outlined text-lg">add</span>
                  {t("settings.vacation.add_day")}
                </button>
              </div>

              {props.schedule.items.length === 0 ? (
                <div class="text-center py-12 border-2 border-dashed  rounded-xl">
                  <span class="material-symbols-outlined text-base-content/30 text-5xl mb-3">
                    event_busy
                  </span>
                  <p class="text-base-content/60 mb-4">
                    No vacation days scheduled
                  </p>
                  <button
                    type="button"
                    onClick={openNewDay}
                    class="btn btn-primary btn-sm gap-2"
                  >
                    <span class="material-symbols-outlined text-lg">add</span>
                    {t("settings.vacation.add_vacation_day")}
                  </button>
                </div>
              ) : (
                <div class="space-y-3">
                  {props.schedule.items.map((item, index) => (
                    <div
                      key={index}
                      class="border  rounded-lg p-4 hover:border-primary/50 transition-all cursor-pointer group"
                      onClick={() => openEditDay(index)}
                    >
                      <div class="flex items-start justify-between gap-4">
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center gap-3 mb-2">
                            <span class="material-symbols-outlined text-warning">
                              {item.payload.type === "closed_all_day"
                                ? "event_busy"
                                : "schedule"}
                            </span>
                            <div>
                              <h4 class="font-semibold text-base-content">
                                {formatDate(item.date)}
                              </h4>
                              {item.reason && (
                                <p class="text-sm text-base-content/60">
                                  {item.reason}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Summary */}
                          <div class="ml-9 space-y-2">
                            {item.payload.type === "closed_all_day" ? (
                              <div class="flex items-center gap-2">
                                <span class="badge badge-error badge-sm">
                                  {t("settings.vacation.closed_all_day")}
                                </span>
                              </div>
                            ) : (
                              <div class="space-y-1">
                                {item.payload.intervals.map((interval, idx) => (
                                  <div
                                    key={idx}
                                    class="flex items-center gap-2 text-sm"
                                  >
                                    <span class="text-base-content/60">
                                      {interval.start} - {interval.end}
                                    </span>
                                    {interval.services?.["dine-in"] && (
                                      <ServiceBadge service="Dine-in" />
                                    )}
                                    {interval.services?.["takeaway"] && (
                                      <ServiceBadge service="Takeaway" />
                                    )}
                                    {interval.services?.["delivery"] && (
                                      <ServiceBadge service="Delivery" />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeVacationDay(index);
                            }}
                            class="btn btn-ghost btn-sm btn-square opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <span class="material-symbols-outlined text-error">
                              delete
                            </span>
                          </button>
                          <span class="material-symbols-outlined text-base-content/40">
                            chevron_right
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Edit Sheet */}
          {editingItem.value && (
            <RightSheet
              isOpen={true}
              onClose={closeSheet}
              title={
                <div class="flex items-center gap-3 flex-1 pr-4">
                  <div class="rounded-full bg-warning/10 text-warning p-3">
                    <span class="material-symbols-outlined text-2xl">
                      beach_access
                    </span>
                  </div>
                  <div>
                    <h2 class="text-xl font-semibold text-accent">
                      {editingItem.value.index === -1
                        ? "Add Vacation Day"
                        : "Edit Vacation Day"}
                    </h2>
                    <p class="text-base-content/70 text-sm">
                      Configure special hours or closures
                    </p>
                  </div>
                </div>
              }
            >
              <div class="flex flex-col h-full">
                <div class="flex-1 overflow-y-auto">
                  <VacationDayForm
                    item={editingItem.value.item}
                    onUpdateField={updateEditingField}
                    onToggleDayType={toggleDayType}
                    onToggleService={toggleService}
                    onAddInterval={addInterval}
                    onRemoveInterval={removeInterval}
                    onUpdateInterval={updateInterval}
                  />
                </div>

                {/* Action Buttons */}
                <div class="flex-shrink-0 border-t  bg-base-100/90 px-6 py-5 backdrop-blur">
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                    <button
                      type="button"
                      class="btn btn-ghost w-full sm:w-auto"
                      onClick={closeSheet}
                    >
                      {t("general.words.cancel")}
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary gap-2 w-full sm:w-auto sm:min-w-[12rem]"
                      onClick={saveDay}
                    >
                      <span class="material-symbols-outlined">save</span>
                      {t("settings.vacation.save_day")}
                    </button>
                  </div>
                </div>
              </div>
            </RightSheet>
          )}
        </>
      );
    },
  },
);
